import angular from "angular";
import addChangeTagDialogTemplate from "./AddChangeTagDialog.template.html";
import CSMCommonWeb from "csm-common-web";
import CSMBooksWeb from "../../../CSMBooksWeb";
import BooksData from "../../data/BooksData";

export default angular
  .module('csm.books.com.AddChangeTagDialog', [
    CSMCommonWeb,
    CSMBooksWeb,
    'books.ui.accountSelector',
    BooksData
  ])
  .service('AddChangeTagDialog', AddChangeTagDialogService)
  .name;
  
/*******************************************************************************
 * Service: AddChangeTagDialog
 *
 * Utility to show a dialog to manage Tags.
 *******************************************************************************/
/* @ngInject */ function AddChangeTagDialogService($q, $uibModal, BooksSvc, BusyScreen) {
  /*************************************************************************
   * show */
  /**
   * Show the modal dialog for adding/modifying Tags.
   *
   * @param   tagID      ID of tag to modify. Create a new tag if null/NaN.
   * @param   typesList  Array of tag types to be restricted to. If null or
   *                     empty list, all types will be shown.
   *
   * @return  Promise    Resolves with new/updated tag object
   *************************************************************************/
  this.show = function (tagID, typesList) {
    BusyScreen.setBusy(true);

    /*--------------------------------------------------*/
    /* Get the org info and check if product is allowed */
    /* to create status tags before opening modal.      */
    /*--------------------------------------------------*/
    return $q
      .all([
        BooksSvc.getOrganization(),
        BooksSvc.getWebConstants('createStatusTag')
      ])
      .then(function (resp) {
        BusyScreen.setBusy(false);

        return $uibModal.open({
          template: addChangeTagDialogTemplate,
          size: 'md',
          backdrop: 'static',
          controller: Controller,
          controllerAs: '$ctrl',
          resolve: {
            tagID: function () {
              return tagID;
            },
            typesList: function () {
              return Array.isArray(typesList) && typesList.length > 0
                ? typesList
                : [];
            },
            org: function () {
              return resp[0].data;
            },
            createStatus: function () {
              return resp[1].data;
            }
          }
        }).result;
      });
  };
}

/*****************************************************************************
 * Controller
 *****************************************************************************/
/* @ngInject */ function Controller($scope, $injector, createStatus, org, tagID, typesList) {
  var $ctrl = angular.extend(this, {
    createStatus: createStatus,
    org: org,
    tagID: tagID,
    typesList: typesList,
    validators: {}
  });

  var injects = { $ctrl: $ctrl, $scope: $scope };

  $ctrl.commit = $injector.invoke(Commit, null, injects);

  /*-------------------*/
  /* Setup the dialog. */
  /*-------------------*/
  $injector.invoke(Init, null, injects);
}

/*******************************************************************************
 * Commit */
/**
 * Handles the add/change request for the Tag. Closes the dialog and returns
 * the tag.
 *******************************************************************************/
/* @ngInject */ function Commit($ctrl, $scope, Alert, BooksSvc, BusyScreen) {
  return function () {
    /*---------------------------*/
    /* Validation failed, abort. */
    /*---------------------------*/
    if (!$ctrl.validators.validate()) return;

    /*---------------------*/
    /* Update the database */
    /*---------------------*/
    BusyScreen.promise(
      BooksSvc.saveTag($ctrl.tag).then(
        function (resp) {
          /*---------------------------------------------*/
          /* Update the ID on the tag if this is an add. */
          /*---------------------------------------------*/
          if ($ctrl.isNewTag) $ctrl.tag.id = resp.data;

          /*-----------------------------------------------*/
          /* Close the modal dialog and send back the tag. */
          /*-----------------------------------------------*/
          $scope.$close($ctrl.tag);

          /*----------------------*/
          /* Handle server error. */
          /*----------------------*/
        },
        function (resp) {
          Alert.error(resp.parsedResponse.message(), 'Save Tag Error');
        }
      )
    );
  };
}

/*******************************************************************************
 * Init */
/**
 * Set up the modal dialog. Filter the list of available tag types, and set up
 * tag if new or retrieve tag information if modifying an existing tag.
 *******************************************************************************/
/* @ngInject */ function Init(
  $ctrl,
  $scope,
  BooksSettings,
  BooksSvc,
  BusyScreen,
  BooksData,
  SERVER,
  Tools
) {
  /*----------------------------------------------------*/
  /* Watch the tag type for changes. If type is status, */
  /* set flag and disable the tag display options.      */
  /*----------------------------------------------------*/
  $scope.$watch('tag.type', function (type) {
    $scope.forStatus = type == SERVER.BooksDBConstants.TT_STATUS;

    if ($scope.forStatus) {
      $scope.tag.showAssigned = false;
      $scope.tag.showNotAssigned = false;
    }
  });

  /** Types List. */
  var mTypes = [
    { type: SERVER.BooksDBConstants.TT_MEMBERSHIP, label: 'Membership' },
    {
      type: SERVER.BooksDBConstants.TT_LIFETIME_MEMBERSHIP,
      label: 'Lifetime Membership'
    },
    { type: SERVER.BooksDBConstants.TT_INDICATOR, label: 'Indicator' },
    { type: SERVER.BooksDBConstants.TT_STATUS, label: 'Status' }
  ];

  $ctrl.isNewTag = Tools.isNaNZero($ctrl.tagID);

  /** Restrict description text length */
  $ctrl.maxDescLength = BooksSettings.FL_NAME;

  /** Returns the title to be displayed in the dialog. */
  $ctrl.dialogTitle = $ctrl.isNewTag ? 'Add New Tag' : 'Change Tag';

  /*-----------------------------------------------------------*/
  /* Returns an indication the type selector should be         */
  /* disabled. The selector is disabled when there is only one */
  /* tag type to chose from or when modifying an existing tag. */
  /*-----------------------------------------------------------*/
  $ctrl.disableType = !$ctrl.isNewTag || $ctrl.typesList.length == 1;

  /*----------------------------------------------*/
  /* Filter types list for creation of a new tag. */
  /*----------------------------------------------*/
  $ctrl.types = mTypes.filter(function (t) {
    if ($ctrl.isNewTag) {
      /*---------------------------------------*/
      /* Restrict list to only types provided. */
      /*---------------------------------------*/
      if ($ctrl.typesList.length > 0)
        return $ctrl.typesList.indexOf(t.type) > -1 ? true : false;

      /*--------------------------------------*/
      /* Membership cannot be created by user */
      /*--------------------------------------*/
      if (
        t.type == SERVER.BooksDBConstants.TT_MEMBERSHIP ||
        t.type == SERVER.BooksDBConstants.TT_LIFETIME_MEMBERSHIP
      )
        return false;

      /*----------------------------------------------*/
      /* Status unavailable if restricted by product. */
      /*----------------------------------------------*/
      if (!$ctrl.createStatus && t.type == SERVER.BooksDBConstants.TT_STATUS)
        return false;
    }
    return true;
  });

  /*------------------*/
  /* Set up a new tag */
  /*------------------*/
  if ($ctrl.isNewTag) {
    $ctrl.tag = new BooksData.Tag({ organizationID: 0 });

    /*------------------------------------------*/
    /* Entities Admin can only add status tags. */
    /*------------------------------------------*/
    if (
      $ctrl.typesList.length == 1 &&
      $ctrl.typesList[0] == SERVER.BooksDBConstants.TT_STATUS
    )
      $ctrl.tag.type = SERVER.BooksDBConstants.TT_STATUS;
  } else {
    /*----------------------------------------------------------*/
    /* Otherwise this is for an edit and the tag will be loaded */
    /*----------------------------------------------------------*/
    BusyScreen.promise(
      BooksSvc.getTag($ctrl.tagID).then(function (resp) {
        $ctrl.tag = new BooksData.Tag(resp.data);
      })
    );
  }

  /*---------------------------------------*/
  /* Only tags of one type can be created, */
  /* select the type and lock selector.    */
  /*---------------------------------------*/
  if ($ctrl.isNewTag && $ctrl.types.length == 1)
    $ctrl.tag.type = $ctrl.types[0].type;
}
